import mutationCustomerRequestResetPassword from '@shc/nuxt-shc-account-layer/graphql/mutations/mutationCustomerRequestResetPassword.graphql'
import mutationCustomerResetPassword from '@shc/nuxt-shc-account-layer/graphql/mutations/mutationCustomerResetPassword.graphql'
import mutationCustomerDeleteAddress from '@shc/nuxt-shc-account-layer/graphql/mutations/mutationCustomerDeleteAddress.graphql'
import mutationCustomerGenerateToken from '~/graphql/mutations/mutationCustomerGenerateToken.graphql'
import type { PasswordCreate } from '~/components/Shc/Ui/Form/Password/Create/formPasswordCreate'
import type { PasswordReset } from '~/components/Shc/Ui/Form/Password/Reset/formPasswordReset'
import type { Register } from '~/components/Shc/Ui/Form/Register/formRegister'
import type { Login } from '~/components/Shc/Ui/Form/Login/formLogin'
import type {
    CustomerAddressInput,
    Mutation,
    MutationCustomerChangeEmailArgs,
    MutationCustomerChangePasswordArgs,
    MutationCustomerDeleteAddressArgs,
    MutationCustomerSubscribeNewsletterAgreementArgs,
    MutationCustomerUpdateAddressArgs,
    MutationCustomerUpdateArgs,
} from '~/graphql/types/graphql'
import mutationCustomerUpdate from '@/graphql/mutations/customerUpdate.graphql'
import mutationCustomerChangeEmail from '@/graphql/mutations/customerChangeEmail.graphql'
import mutationCustomerChangePassword from '@/graphql/mutations/customerChangePassword.graphql'
import mutationCustomerCreateAddress from '@/graphql/mutations/customerCreateAddress.graphql'
import mutationCustomerUpdateAddress from '@/graphql/mutations/customerUpdateAddress.graphql'
import mutationCustomerSubscribeNewsletterAgreement from '@/graphql/mutations/customerSubscribeNewsletterAgreement.graphql'
import mutationWishlistUpdateProducts from '@/graphql/mutations/mutationWishlistUpdateProducts.graphql'
import mutationWishlistAddItemsToCart from '@/graphql/mutations/mutationWishlistAddItemsToCart.graphql'
import mutationWishlistShare from '@/graphql/mutations/mutationWishlistShare.graphql'
import mutationCustomerCreate from '@/graphql/mutations/mutationCustomerCreate.graphql'
import mutationWishlistAddProducts from '@/graphql/mutations/mutationWishlistAddProducts.graphql'
import mutationWishlistRemoveProducts from '@/graphql/mutations/mutationWishlistRemoveProducts.graphql'

export default defineNuxtPlugin({
    enforce: 'post',
    async setup() {
        const { $bus, $i18n, $apolloHelpers } = useNuxtApp()
        const { CART_ID, URL_LOGIN_SUCCESS } = useAppConfig()
        const cookieCartId = useCookie<string>(CART_ID)
        const { isUserLoggedIn, logOut } = useUser()

        function handleLoginSuccess(
            token: string,
            emailAddress: string,
            cartId?: string | null,
            customerId?: number | null,
        ) {
            $apolloHelpers.onLogin(token)
            cookieCartId.value = cartId
            $bus.$emit('customerLoginSuccess', { customerId, emailAddress })
            navigateTo(`/${$i18n.locale.value}${URL_LOGIN_SUCCESS}`, {
                external: true,
            })
        }

        function handleError(error: Error) {
            $bus.$emit('notification', {
                message: error.message,
                type: 'error',
            })
        }

        async function customerGenerateToken(data: Login) {
            const { mutate: customerGenerateTokenMutate } = useMutation<Pick<Mutation, 'customerGenerateToken'>>(
                mutationCustomerGenerateToken,
                {
                    variables: {
                        username: data.username,
                        password: data.password,
                    },
                    context: {
                        headers: {
                            ...data.headers,
                        },
                    },
                },
            )

            const response = await customerGenerateTokenMutate()

            return response?.data?.customerGenerateToken
        }

        async function customerCreate(data: Register) {
            const { mutate: customerCreateMutate } = useMutation<Pick<Mutation, 'customerCreate'>>(
                mutationCustomerCreate,
                {
                    variables: {
                        customer: {
                            firstName: data.firstName,
                            lastName: data.lastName,
                            email: data.email,
                            password: data.password,
                            registrationAgreements: data.registrationAgreements,
                            newsletterAgreements: data.newsletterAgreements,
                            phoneNumber: data.phoneNumber,
                        },
                    },
                    context: {
                        headers: {
                            ...data.headers,
                        },
                    },
                },
            )

            const response = await customerCreateMutate()

            return response?.data?.customerCreate
        }

        async function customerRequestResetPassword(data: PasswordReset) {
            const { mutate: customerRequestResetPasswordMutate } = useMutation<
                Pick<Mutation, 'customerRequestResetPassword'>
            >(mutationCustomerRequestResetPassword, {
                variables: { email: data.email },
                context: {
                    headers: {
                        ...data.headers,
                    },
                },
            })

            const response = await customerRequestResetPasswordMutate()

            return response?.data?.customerRequestResetPassword
        }

        async function customerResetPassword(data: PasswordCreate) {
            const { mutate: customerResetPasswordMutate } = useMutation<Pick<Mutation, 'customerResetPassword'>>(
                mutationCustomerResetPassword,
                {
                    variables: {
                        email: data.email,
                        token: data.token,
                        newPassword: data.newPassword,
                    },
                    context: {
                        headers: {
                            ...data.headers,
                        },
                    },
                },
            )

            const response = await customerResetPasswordMutate()
            return response?.data?.customerResetPassword
        }

        async function customerCreateAddress(data: CustomerAddressInput) {
            const { mutate: customerCreateAddressMutate } = useMutation<Pick<Mutation, 'customerCreateAddress'>>(
                mutationCustomerCreateAddress,
                {
                    variables: {
                        address: {
                            firstName: data.firstName,
                            lastName: data.lastName,
                            address: data.address,
                            city: data.city,
                            postcode: data.postcode,
                            telephone: data.telephone,
                            regionId: data.regionId,
                            countryId: 'PL',
                            vatId: data.vatId,
                            company: data.company,
                            defaultBilling: data.defaultBilling,
                            defaultShipping: data.defaultShipping,
                        },
                    },
                },
            )

            const response = await customerCreateAddressMutate()

            return response?.data?.customerCreateAddress
        }

        async function customerUpdateAddress(data: MutationCustomerUpdateAddressArgs) {
            const { address } = data
            if (!address) {
                return
            }

            const { mutate: customerUpdateAddressMutate } = useMutation<Pick<Mutation, 'customerUpdateAddress'>>(
                mutationCustomerUpdateAddress,
                {
                    variables: {
                        addressId: data.addressId,
                        address: {
                            firstName: address.firstName,
                            lastName: address.lastName,
                            address: address.address,
                            city: address.city,
                            postcode: address.postcode,
                            telephone: address.telephone,
                            regionId: address.regionId,
                            countryId: 'PL',
                            vatId: address.vatId,
                            company: address.company,
                            defaultBilling: address.defaultBilling,
                            defaultShipping: address.defaultShipping,
                        },
                    },
                },
            )

            const response = await customerUpdateAddressMutate()

            return response?.data?.customerUpdateAddress
        }

        async function customerDeleteAddress(data: MutationCustomerDeleteAddressArgs) {
            const { addressId } = data
            const { mutate: customerDeleteAddressMutate } = useMutation<Pick<Mutation, 'customerDeleteAddress'>>(
                mutationCustomerDeleteAddress,
                {
                    variables: {
                        addressId,
                    },
                },
            )

            const response = await customerDeleteAddressMutate()

            return response?.data?.customerDeleteAddress
        }

        async function customerUpdate(data: MutationCustomerUpdateArgs) {
            const { customer } = data
            const { mutate: customerUpdateMutate } = useMutation<Pick<Mutation, 'customerUpdate'>>(
                mutationCustomerUpdate,
                {
                    variables: {
                        customer,
                    },
                },
            )

            const response = await customerUpdateMutate()

            return response?.data?.customerUpdate
        }

        async function customerChangeEmail(data: MutationCustomerChangeEmailArgs) {
            const { mutate: customerChangeEmailMutate } = useMutation<Pick<Mutation, 'customerChangeEmail'>>(
                mutationCustomerChangeEmail,
                {
                    variables: {
                        ...data,
                    },
                },
            )

            const response = await customerChangeEmailMutate()

            return response?.data?.customerChangeEmail
        }

        async function customerChangePassword(data: MutationCustomerChangePasswordArgs) {
            const { mutate: customerChangePasswordMutate } = useMutation<Pick<Mutation, 'customerChangePassword'>>(
                mutationCustomerChangePassword,
                {
                    variables: {
                        ...data,
                    },
                },
            )

            const response = await customerChangePasswordMutate()

            return response?.data?.customerChangePassword
        }

        $bus.$on('customerLogin', async (data: Login) => {
            try {
                const result = await customerGenerateToken(data)

                if (result?.token) {
                    handleLoginSuccess(result.token, data.username, result?.cartId, result.customerId)
                }
            } catch (error) {
                if (error instanceof Error) {
                    handleError(error)
                } else {
                    console.error('Error in customerLogin', error)
                }

                $bus.$emit('customerError')
            } finally {
                $bus.$emit('customerLoginDone')
            }
        })

        $bus.$on('customerCreate', async (customerCreateData: Register) => {
            try {
                const result = await customerCreate(customerCreateData)
                const { phoneNumber, email, newsletterAgreements } = customerCreateData

                if (result) {
                    const { id } = result.customer
                    $bus.$emit('customerCreateSuccess', { phoneNumber, email, newsletterAgreements, id })
                }
            } catch (error) {
                if (error instanceof Error) {
                    handleError(error)
                } else {
                    console.error('Error in customerCreate', error)
                }

                $bus.$emit('customerError')
            } finally {
                $bus.$emit('customerCreateDone')
            }
        })

        $bus.$on('customerRequestResetPassword', async (data: PasswordReset) => {
            try {
                const result = await customerRequestResetPassword(data)

                if (result) {
                    $bus.$emit('customerRequestResetPasswordSuccess')
                }
            } catch (error) {
                if (error instanceof Error) {
                    handleError(error)
                } else {
                    console.error('Error in customerRequestResetPassword', error)
                }

                $bus.$emit('customerError')
            } finally {
                $bus.$emit('customerRequestResetPasswordDone')
            }
        })

        $bus.$on('customerResetPassword', async (data: PasswordCreate) => {
            try {
                const result = await customerResetPassword(data)

                if (result) {
                    $bus.$emit('customerResetPasswordSuccess')
                }
            } catch (error) {
                if (error instanceof Error) {
                    handleError(error)
                } else {
                    console.error('Error in customerResetPassword', error)
                }

                $bus.$emit('customerError')
            } finally {
                $bus.$emit('customerResetPasswordDone')
            }
        })

        $bus.$on('wishlistAddProducts', async ({ sku, quantity = 1, wishlistId = '0', limit = 10, page = 1 }) => {
            if (!(await isUserLoggedIn())) {
                await logOut()
                return
            }

            const { mutate: wishlistAddProductsMutate } = useMutation<Pick<Mutation, 'wishlistAddProducts'>>(
                mutationWishlistAddProducts,
                {
                    variables: {
                        wishlistItems: [
                            {
                                sku,
                                quantity,
                            },
                        ],
                        wishlistId,
                        limit,
                        page,
                    },
                },
            )

            try {
                const result = await wishlistAddProductsMutate()

                if (result?.data?.wishlistAddProducts) {
                    const addedProduct = result.data.wishlistAddProducts.wishlist.items.find(
                        (item) => item?.product?.sku === sku,
                    )

                    $bus.$emit('wishlistAddProductsSuccess', {
                        data: result.data.wishlistAddProducts,
                        sku,
                        product: addedProduct?.product,
                    })
                }
            } catch (err) {
                if (err instanceof Error) {
                    $bus.$emit('notification', {
                        message: err.message,
                        type: 'error',
                    })
                } else {
                    console.error(err)
                }
            }
        })

        $bus.$on('wishlistRemoveProducts', async ({ wishlistId, wishlistItemsIds, limit = 10, page = 1 }) => {
            const { mutate: wishlistRemoveProductsMutate } = useMutation<Pick<Mutation, 'wishlistRemoveProducts'>>(
                mutationWishlistRemoveProducts,
                {
                    variables: {
                        wishlistId,
                        wishlistItemsIds,
                        limit,
                        page,
                    },
                },
            )

            try {
                const result = await wishlistRemoveProductsMutate()

                if (result?.data?.wishlistRemoveProducts) {
                    $bus.$emit('notification', {
                        message: $i18n.t('Bbk.wishlist.removeSuccess'),
                        type: 'success',
                    })
                    $bus.$emit('wishlistRemoveProductsSuccess', result.data.wishlistRemoveProducts)
                }
            } catch (err) {
                if (err instanceof Error) {
                    $bus.$emit('notification', {
                        message: err.message,
                        type: 'error',
                    })
                } else {
                    console.error(err)
                }
            }
        })

        $bus.$on('wishlistUpdateProducts', async ({ wishlistId, wishlistItems, limit = 10, page = 1 }) => {
            const { mutate: wishlistUpdateProductsMutate } = useMutation<Pick<Mutation, 'wishlistUpdateProducts'>>(
                mutationWishlistUpdateProducts,
                {
                    variables: {
                        wishlistId,
                        wishlistItems,
                        limit,
                        page,
                    },
                },
            )

            try {
                const result = await wishlistUpdateProductsMutate()

                if (result?.data?.wishlistUpdateProducts) {
                    $bus.$emit('notification', {
                        message: $i18n.t('Bbk.wishlist.updateSuccess'),
                        type: 'success',
                    })
                    $bus.$emit('wishlistUpdateProductsSuccess', result.data.wishlistUpdateProducts)
                }
            } catch (err) {
                if (err instanceof Error) {
                    $bus.$emit('notification', {
                        message: err.message,
                        type: 'error',
                    })
                } else {
                    console.error(err)
                }
            } finally {
                $bus.$emit('wishlistUpdateProductsDone')
            }
        })

        $bus.$on('wishlistItemsToCart', async ({ wishlistId, wishlistItemId, limit = 10, page = 1 }) => {
            const { mutate: wishlistItemsToCartMutate } = useMutation<Pick<Mutation, 'wishlistAddItemsToCart'>>(
                mutationWishlistAddItemsToCart,
                {
                    variables: {
                        wishlistId,
                        wishlistItemIds: wishlistItemId ? [wishlistItemId] : [],
                        limit,
                        page,
                    },
                },
            )

            try {
                const result = await wishlistItemsToCartMutate()

                if (result?.data?.wishlistAddItemsToCart?.status) {
                    $bus.$emit('notification', {
                        message: wishlistItemId
                            ? $i18n.t('Bbk.wishlist.oneItemAddedToCart')
                            : $i18n.t('Bbk.wishlist.allItemsAddedToCart'),
                        type: 'success',
                    })
                    $bus.$emit('wishlistItemsToCartSuccess', result.data.wishlistAddItemsToCart)
                } else {
                    $bus.$emit('notification', {
                        message: $i18n.t('Bbk.page.error.message'),
                        type: 'error',
                    })
                }
            } catch (err) {
                if (err instanceof Error) {
                    $bus.$emit('notification', {
                        message: err.message,
                        type: 'error',
                    })
                } else {
                    console.error(err)
                }
            } finally {
                $bus.$emit('wishlistItemsToCartDone')
            }
        })

        $bus.$on('wishlistShare', async ({ wishlistId, emails, message }) => {
            const { mutate: wishlistShareMutate } = useMutation<Pick<Mutation, 'wishlistShare'>>(
                mutationWishlistShare,
                {
                    variables: {
                        input: {
                            wishlistId,
                            emails,
                            message,
                        },
                    },
                },
            )

            try {
                const result = await wishlistShareMutate()

                if (result?.data?.wishlistShare) {
                    $bus.$emit('notification', {
                        message: $i18n.t('Bbk.wishlist.shareSuccess'),
                        type: 'success',
                    })
                    $bus.$emit('wishlistShareSuccess', result.data.wishlistShare)
                }
            } catch (err) {
                if (err instanceof Error) {
                    $bus.$emit('notification', {
                        message: err.message,
                        type: 'error',
                    })
                } else {
                    console.error(err)
                }
            } finally {
                $bus.$emit('wishlistShareDone')
            }
        })

        $bus.$on('customerAddressCreate', async (data: CustomerAddressInput) => {
            try {
                const result = await customerCreateAddress(data)

                if (result) {
                    $bus.$emit('customerAddressCreateSuccess')
                    $bus.$emit('notification', {
                        type: 'success',
                        message: $i18n.t('Bbk.customer.address.success'),
                    })
                }
            } catch (error) {
                if (error instanceof Error) {
                    handleError(error)
                } else {
                    console.error('Error in customerAddressCreate', error)
                }
            } finally {
                $bus.$emit('customerAddressCreateDone')
            }
        })

        $bus.$on('customerAddressUpdate', async (data: MutationCustomerUpdateAddressArgs) => {
            try {
                const result = await customerUpdateAddress(data)

                if (result) {
                    $bus.$emit('customerAddressUpdateSuccess')
                    $bus.$emit('notification', {
                        message: $i18n.t('Bbk.customer.address.success'),
                        type: 'success',
                    })
                }
            } catch (error) {
                if (error instanceof Error) {
                    handleError(error)
                } else {
                    console.error('Error in customerAddressUpdate', error)
                }
            } finally {
                $bus.$emit('customerAddressUpdateDone')
            }
        })

        $bus.$on('customerAddressDelete', async (data: MutationCustomerDeleteAddressArgs) => {
            try {
                const result = await customerDeleteAddress(data)

                if (result) {
                    $bus.$emit('customerAddressDeleteSuccess', data)
                    $bus.$emit('notification', {
                        type: 'success',
                        message: $i18n.t('Bbk.customer.address.removeSuccess'),
                    })
                }
            } catch (error) {
                if (error instanceof Error) {
                    handleError(error)
                } else {
                    console.error('Error in customerAddressDelete', error)
                }
            } finally {
                $bus.$emit('customerAddressDeleteDone')
            }
        })

        $bus.$on('customerUpdate', async (data: MutationCustomerUpdateArgs) => {
            try {
                const result = await customerUpdate(data)

                if (result) {
                    $bus.$emit('customerUpdateSuccess', data)
                    $bus.$emit('notification', {
                        type: 'success',
                        message: $i18n.t('Bbk.customer.myData.updateSuccess'),
                    })
                }
            } catch (error) {
                if (error instanceof Error) {
                    handleError(error)
                } else {
                    console.error('Error in customerUpdate', error)
                }
            } finally {
                $bus.$emit('customerUpdateDone')
            }
        })

        $bus.$on('customerChangeEmail', async (data: MutationCustomerChangeEmailArgs) => {
            try {
                const result = await customerChangeEmail(data)

                if (result) {
                    $bus.$emit('customerChangeEmailSuccess')
                    $bus.$emit('notification', {
                        type: 'success',
                        message: $i18n.t('Bbk.customer.myData.changeEmailSuccess'),
                    })
                }
            } catch (error) {
                if (error instanceof Error) {
                    handleError(error)
                } else {
                    console.error('Error in customerChangeEmail', error)
                }
            } finally {
                $bus.$emit('customerChangeEmailDone')
            }
        })

        $bus.$on('customerChangePassword', async (data: MutationCustomerChangePasswordArgs) => {
            try {
                const result = await customerChangePassword(data)

                if (result) {
                    $bus.$emit('notification', {
                        type: 'success',
                        message: $i18n.t('Bbk.customer.myData.changePasswordSuccess'),
                    })
                }
            } catch (error) {
                if (error instanceof Error) {
                    handleError(error)
                } else {
                    console.error('Error in customerChangePassword', error)
                }
            } finally {
                $bus.$emit('customerChangePasswordDone')
            }
        })

        $bus.$on(
            'customerSubscribeNewsletterAgreement',
            async (data: MutationCustomerSubscribeNewsletterAgreementArgs) => {
                const { mutate: customerSubscribeNewsletterAgreementMutate } = useMutation(
                    mutationCustomerSubscribeNewsletterAgreement,
                    {
                        variables: {
                            codes: data.codes,
                            phoneNumber: data.phoneNumber,
                        },
                    },
                )

                try {
                    const result = await customerSubscribeNewsletterAgreementMutate()

                    if (result?.data?.customerSubscribeNewsletterAgreement) {
                        $bus.$emit('notification', {
                            message: $i18n.t('Bbk.customer.newsletter.subscribed'),
                            type: 'success',
                        })
                    }
                } catch (err) {
                    if (err instanceof Error) {
                        console.error(err)
                        $bus.$emit('notification', {
                            message: err.message,
                            type: 'error',
                        })
                    }
                } finally {
                    $bus.$emit('customerSubscribeNewsletterAgreementDone')
                }
            },
        )
    },
})
